/*
 * Julvärmen theme core.
 * Shared by every jv-* section: cart API + section re-rendering, dialogs (drawers, sheets, modals),
 * money formatting, scroll reveal, wishlist, toasts and small form helpers.
 * Sections add their own behaviour in their own asset files and talk to this core through window.JV
 * and the `jv:*` events documented in Theme/BUILD-SPEC.md.
 */
(() => {
  'use strict';

  const config = window.JVConfig || {};
  const routes = config.routes || {};
  const strings = config.strings || {};
  const JV = (window.JV = window.JV || {});
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  JV.config = config;
  JV.t = (key, fallback = '') => strings[key] || fallback;

  /* ------------------------------------------------------------------ events */
  JV.emit = (name, detail = {}, target = document) =>
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));

  /* ------------------------------------------------------------------ money */
  // The layout renders a sample amount (1234.56) in the visitor's currency and locale; we copy its
  // prefix, suffix and separators so client-side totals look exactly like Liquid's `money` filter.
  const moneyPattern = (() => {
    const sample = String(config.moneySample || '€1.234,56');
    const match = sample.match(/1(\D?)234(?:(\D)(\d{2}))?/);
    if (!match) return { prefix: '', suffix: ' ' + (config.currency || ''), thousands: ',', decimal: '.', decimals: 2 };
    const start = sample.indexOf(match[0]);
    return {
      prefix: sample.slice(0, start),
      suffix: sample.slice(start + match[0].length),
      thousands: match[1] || '',
      decimal: match[2] || '.',
      decimals: match[3] ? 2 : 0,
    };
  })();

  JV.formatMoney = (cents) => {
    const p = moneyPattern;
    const value = Math.round(Number(cents) || 0) / 100;
    const fixed = value.toFixed(p.decimals);
    let [whole, fraction] = fixed.split('.');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, p.thousands);
    return p.prefix + whole + (p.decimals ? p.decimal + fraction : '') + p.suffix;
  };

  /* ------------------------------------------------------------------ fetch */
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  async function postJSON(url, body) {
    const response = await fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    if (!response.ok || data.status) {
      const message = data.description || data.message || JV.t('error', 'Something went wrong. Please try again.');
      const err = new Error(typeof message === 'string' ? message : JV.t('error'));
      err.status = response.status || data.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  JV.fetchSection = async (sectionId, url = window.location.pathname + window.location.search) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section ${sectionId} failed to load`);
    return response.text();
  };

  JV.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------------------ cart */
  // Any element with data-jv-cart-section="{{ section.id }}" is re-rendered after every cart change.
  // Inside it, each element marked data-jv-cart-region="name" is swapped with the fresh copy, so the
  // section's outer element (an open <dialog>, for example) keeps its state.
  const cartSectionIds = () =>
    [...new Set([...document.querySelectorAll('[data-jv-cart-section]')].map((el) => el.dataset.jvCartSection))].slice(0, 5);

  JV.renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = JV.parseHTML(html);
      document.querySelectorAll(`[data-jv-cart-section="${CSS.escape(id)}"]`).forEach((current) => {
        const fresh = doc.querySelector(`[data-jv-cart-section="${CSS.escape(id)}"]`);
        if (!fresh) return;
        const regions = current.querySelectorAll('[data-jv-cart-region]');
        if (!regions.length) {
          current.innerHTML = fresh.innerHTML;
          return;
        }
        regions.forEach((region) => {
          const replacement = fresh.querySelector(`[data-jv-cart-region="${CSS.escape(region.dataset.jvCartRegion)}"]`);
          if (replacement) region.replaceWith(replacement);
        });
      });
    });
    JV.reveal(document);
    if (JV.wishlist) JV.wishlist.sync(document);
    JV.emit('jv:cart:rendered', { sections: Object.keys(sections) });
  };

  JV.updateCartCount = (count) => {
    document.querySelectorAll('[data-jv-cart-count]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count < 1;
    });
    document.querySelectorAll('[data-jv-cart-label]').forEach((el) => {
      const template = el.dataset.jvCartLabel || '';
      el.setAttribute('aria-label', template.replace('[count]', count));
    });
  };

  let cartQueue = Promise.resolve();
  const queued = (task) => {
    const run = cartQueue.then(task, task);
    cartQueue = run.catch(() => {});
    return run;
  };

  JV.cart = {
    async get() {
      const response = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },

    /** items: [{ id, quantity, properties?, selling_plan? }] */
    add(items, options = {}) {
      return queued(async () => {
        const sections = cartSectionIds();
        const data = await postJSON(`${routes.cartAdd || '/cart/add'}.js`, {
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });
        JV.renderCartSections(data.sections);
        const cart = await JV.cart.get();
        JV.updateCartCount(cart.item_count);
        JV.emit('jv:cart:change', { cart, action: 'add', items: data.items || [], source: options.source || 'jv' });
        if (options.openDrawer !== false) JV.cart.open({ source: options.source });
        return cart;
      });
    },

    /** line: 1-based line index, or pass { id: 'line-item-key' } */
    change({ line, id, quantity, properties }, options = {}) {
      return queued(async () => {
        const body = { quantity, sections: cartSectionIds().join(','), sections_url: window.location.pathname };
        if (id) body.id = id;
        else body.line = line;
        if (properties) body.properties = properties;
        const cart = await postJSON(`${routes.cartChange || '/cart/change'}.js`, body);
        JV.renderCartSections(cart.sections);
        JV.updateCartCount(cart.item_count);
        JV.emit('jv:cart:change', { cart, action: 'change', source: options.source || 'jv' });
        return cart;
      });
    },

    /** payload: { updates?, attributes?, note?, discount? } */
    update(payload, options = {}) {
      return queued(async () => {
        const cart = await postJSON(`${routes.cartUpdate || '/cart/update'}.js`, {
          ...payload,
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        });
        JV.renderCartSections(cart.sections);
        JV.updateCartCount(cart.item_count);
        JV.emit('jv:cart:change', { cart, action: 'update', source: options.source || 'jv' });
        return cart;
      });
    },

    /** Re-render every cart section from the server (after changes made by apps or Horizon blocks). */
    async refresh() {
      const ids = cartSectionIds();
      if (ids.length) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', ids.join(','));
        const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (response.ok) JV.renderCartSections(await response.json());
      }
      const cart = await JV.cart.get();
      JV.updateCartCount(cart.item_count);
      JV.emit('jv:cart:change', { cart, action: 'refresh', source: 'external' });
      return cart;
    },

    open(detail = {}) {
      if (config.cartType === 'page') {
        if (!/\/cart\/?$/.test(window.location.pathname)) window.location.href = routes.cart || '/cart';
        return;
      }
      JV.emit('jv:cart:open', detail);
    },
  };

  // Changes made outside the D&P components (apps, Horizon blocks, Shopify standard actions).
  ['shopify:cart:lines-update', 'shopify:cart:note-update', 'shopify:cart:discount-update'].forEach((name) =>
    document.addEventListener(name, (event) => {
      if (event.detail && event.detail.source === 'jv') return;
      JV.cart.refresh().catch(() => {});
    })
  );

  /* ------------------------------------------------------------------ buttons that add one variant */
  // <button data-jv-add-variant="{{ variant.id }}" data-jv-add-quantity="1">…</button>
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-jv-add-variant]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const id = Number(button.dataset.jvAddVariant);
    const quantity = Number(button.dataset.jvAddQuantity || 1);
    JV.setBusy(button, true);
    try {
      await JV.cart.add([{ id, quantity }], { source: button.dataset.jvSource || 'card' });
    } catch (error) {
      JV.toast(error.message, { tone: 'error' });
    } finally {
      JV.setBusy(button, false);
    }
  });

  JV.setBusy = (button, busy) => {
    if (!button) return;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    button.disabled = busy;
    if (busy) button.dataset.jvWasBusy = 'true';
    else delete button.dataset.jvWasBusy;
  };

  /* ------------------------------------------------------------------ dialogs */
  // Every drawer, bottom sheet and modal is a native <dialog class="jv-dialog …"> so the browser
  // handles focus trapping, inert background and Escape. We add .is-open one frame after showModal()
  // so the CSS transition runs, and wait for it before close().
  // The lock is derived from the dialogs that are actually open, so a dialog removed by a section
  // re-render (theme editor) can never leave the page locked.
  const syncScrollLock = () => {
    const anyOpen = [...document.querySelectorAll('dialog.jv-dialog[open]')].some((d) => !d._dpClosing);
    document.documentElement.classList.toggle('jv-scroll-locked', anyOpen);
  };
  JV.syncScrollLock = syncScrollLock;

  JV.openDialog = (dialog, opener) => {
    if (!dialog) return;
    if (dialog.open && dialog._dpClosing) {
      // Re-opened during the close animation: cancel the close.
      window.clearTimeout(dialog._dpCloseTimer);
      dialog._dpClosing = false;
      dialog.classList.add('is-open');
      syncScrollLock();
      return;
    }
    if (dialog.open) return;
    dialog._dpOpener = opener || document.activeElement;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    syncScrollLock();
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    JV.emit('jv:dialog:open', { id: dialog.id }, dialog);
  };

  JV.closeDialog = (dialog) => {
    if (!dialog || !dialog.open || dialog._dpClosing) return;
    dialog._dpClosing = true;
    dialog.classList.remove('is-open');
    syncScrollLock();
    const finish = () => {
      dialog._dpClosing = false;
      if (dialog.open) dialog.close();
      syncScrollLock();
      const toasts = dialog.querySelector('#jv-toasts');
      if (toasts) document.body.appendChild(toasts);
      const opener = dialog._dpOpener;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true });
      JV.emit('jv:dialog:close', { id: dialog.id }, dialog);
    };
    const duration = reducedMotion.matches ? 0 : parseFloat(getComputedStyle(dialog).getPropertyValue('--jv-dialog-duration')) || 320;
    dialog._dpCloseTimer = window.setTimeout(finish, duration);
  };

  // Openers: <button data-jv-open="DialogId">, closers: <button data-jv-close> inside the dialog.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-jv-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.jvOpen);
      if (dialog) {
        event.preventDefault();
        JV.openDialog(dialog, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-jv-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        JV.closeDialog(dialog);
      }
      return;
    }
    // A click on the ::backdrop lands on the dialog element itself. Ignore drags that started inside.
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('jv-dialog') && (pressStartedOn === null || pressStartedOn === event.target)) {
      JV.closeDialog(event.target);
    }
    pressStartedOn = null;
  });
  let pressStartedOn = null;
  document.addEventListener('pointerdown', (event) => { pressStartedOn = event.target; }, true);

  // Escape: let our close animation run instead of the instant native close.
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target instanceof HTMLDialogElement && event.target.classList.contains('jv-dialog')) {
        event.preventDefault();
        JV.closeDialog(event.target);
      }
    },
    true
  );

  /* ------------------------------------------------------------------ reveal on scroll */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  JV.reveal = (root = document) => {
    const items = root.querySelectorAll('[data-jv-reveal]:not(.is-revealed)');
    if (!config.animations || reducedMotion.matches || designMode || !revealObserver) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    items.forEach((el) => revealObserver.observe(el));
  };

  if (config.animations && !reducedMotion.matches && !designMode) {
    document.documentElement.classList.add('jv-anim');
  }

  /* ------------------------------------------------------------------ toast */
  JV.toast = (message, { tone = 'info', timeout = 3600 } = {}) => {
    if (!message) return;
    let region = document.getElementById('jv-toasts');
    if (!region) {
      region = document.createElement('div');
      region.id = 'jv-toasts';
      region.className = 'jv-toasts';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    // Modal dialogs live in the top layer; a toast outside the open dialog would sit behind it (and be inert).
    const openDialogs = [...document.querySelectorAll('dialog[open]')];
    const host = openDialogs.length ? openDialogs[openDialogs.length - 1] : document.body;
    if (region.parentElement !== host) host.appendChild(region);
    const toast = document.createElement('div');
    toast.className = `jv-toast jv-toast--${tone}`;
    toast.textContent = message;
    region.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 400);
    }, timeout);
  };

  /* ------------------------------------------------------------------ wishlist (this browser only) */
  const WISHLIST_KEY = 'jv-wishlist';
  const readWishlist = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch (error) {
      return [];
    }
  };
  const writeWishlist = (list) => {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage unavailable: the wishlist simply does not persist */
    }
  };

  JV.wishlist = {
    list: readWishlist,
    has: (handle) => readWishlist().includes(handle),
    toggle(handle) {
      const list = readWishlist();
      const index = list.indexOf(handle);
      if (index === -1) list.unshift(handle);
      else list.splice(index, 1);
      writeWishlist(list.slice(0, 60));
      JV.wishlist.sync();
      JV.emit('jv:wishlist:change', { handle, active: index === -1, list });
      return index === -1;
    },
    sync(root = document) {
      const list = readWishlist();
      root.querySelectorAll('[data-jv-wishlist-toggle]').forEach((button) => {
        const active = list.includes(button.dataset.jvWishlistToggle);
        button.setAttribute('aria-pressed', String(active));
        const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
        if (label) button.setAttribute('aria-label', label);
      });
      document.querySelectorAll('[data-jv-wishlist-count]').forEach((el) => {
        el.textContent = String(list.length);
        el.hidden = list.length < 1;
      });
    },
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-jv-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const added = JV.wishlist.toggle(button.dataset.jvWishlistToggle);
    JV.toast(added ? JV.t('wishlistAdded', 'Saved to your wishlist') : JV.t('wishlistRemoved', 'Removed from your wishlist'));
  });

  /* ------------------------------------------------------------------ quantity steppers */
  // <div class="jv-qty" data-jv-qty><button data-jv-qty-step="-1">…</button><input …><button data-jv-qty-step="1">…</button></div>
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-jv-qty-step]');
    if (!step) return;
    const wrap = step.closest('[data-jv-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    event.preventDefault();
    const min = input.min !== '' ? Number(input.min) : 0;
    const max = input.max !== '' ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(step.dataset.jvQtyStep)));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------ smooth <details> */
  // Opt in with <details class="jv-acc__item" data-jv-smooth>.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('details[data-jv-smooth] > summary');
    if (!summary || reducedMotion.matches) return;
    const details = summary.parentElement;
    const body = summary.nextElementSibling;
    if (!body || details._dpAnimating) return;
    event.preventDefault();
    details._dpAnimating = true;
    if (details.open) {
      const start = body.offsetHeight;
      const animation = body.animate({ height: [`${start}px`, '0px'], opacity: [1, 0] }, { duration: 220, easing: 'ease-out' });
      animation.onfinish = () => {
        details.open = false;
        details._dpAnimating = false;
      };
    } else {
      details.open = true;
      const end = body.offsetHeight;
      const animation = body.animate({ height: ['0px', `${end}px`], opacity: [0, 1] }, { duration: 260, easing: 'ease-out' });
      animation.onfinish = () => {
        details._dpAnimating = false;
      };
    }
  });

  /* ------------------------------------------------------------------ Shopify standard actions */
  const configureStandardActions = () => {
    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || !actions.openCart || typeof actions.openCart.configure !== 'function') return;
    actions.openCart.configure({
      async handler() {
        JV.cart.open({ source: 'standard-action' });
      },
    });
  };
  if (window.Shopify && window.Shopify.actions) configureStandardActions();
  else document.addEventListener('DOMContentLoaded', configureStandardActions, { once: true });

  /* ------------------------------------------------------------------ rails (sliders) */
  // <jv-rail> wraps [data-jv-rail-track] plus optional [data-jv-rail-prev], [data-jv-rail-next] and [data-jv-rail-bar].
  // Swipe works natively; the buttons scroll one item with a short eased animation; the bar shows position.
  // When everything fits, the rail gets .is-fit and the controls hide.
  if (!customElements.get('jv-rail')) {
    class JvRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-jv-rail-track]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-jv-rail-prev]');
        this.next = this.querySelector('[data-jv-rail-next]');
        this.bar = this.querySelector('[data-jv-rail-bar]');
        this.onScroll = () => this.update();
        this.onPrev = () => this.go(-1);
        this.onNext = () => this.go(1);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);
        this.ro = new ResizeObserver(() => this.update());
        this.ro.observe(this.track);
        this.update();
      }
      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
        this.ro?.disconnect();
        window.clearTimeout(this.timer);
      }
      step() {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 12;
        return item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
      }
      go(dir) {
        const t = this.track;
        const max = Math.max(0, t.scrollWidth - t.clientWidth);
        const from = t.scrollLeft;
        const to = Math.max(0, Math.min(max, from + dir * this.step()));
        if (to === from) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const snap = t.style.scrollSnapType;
        t.style.scrollSnapType = 'none';
        window.clearTimeout(this.timer);
        const start = performance.now();
        const dur = reduce ? 0 : 320;
        const tick = () => {
          const k = dur ? Math.min(1, (performance.now() - start) / dur) : 1;
          t.scrollLeft = from + (to - from) * (1 - Math.pow(1 - k, 3));
          this.update();
          if (k < 1) this.timer = window.setTimeout(tick, 16);
          else t.style.scrollSnapType = snap;
        };
        tick();
      }
      update() {
        const t = this.track;
        const max = Math.max(0, t.scrollWidth - t.clientWidth);
        const fit = max <= 2;
        this.classList.toggle('is-fit', fit);
        const atStart = t.scrollLeft <= 4;
        const atEnd = t.scrollLeft >= max - 4;
        if (this.prev) this.prev.setAttribute('aria-disabled', String(atStart));
        if (this.next) this.next.setAttribute('aria-disabled', String(atEnd));
        if (this.bar && t.scrollWidth) {
          const ratio = Math.min(1, t.clientWidth / t.scrollWidth);
          const pct = max ? t.scrollLeft / max : 0;
          this.bar.style.width = (ratio * 100).toFixed(1) + '%';
          this.bar.style.left = (pct * (100 - ratio * 100)).toFixed(1) + '%';
        }
      }
    }
    customElements.define('jv-rail', JvRail);
  }

  /* ------------------------------------------------------------------ copy to clipboard */
  // <button data-jv-copy="CODE" data-copied-label="Kopierad">Kopiera koden</button>
  JV.copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      area.remove();
      return ok;
    }
  };
  // A copy shows a clear confirmation in the button itself (green, tick, "Koden är kopierad") and is read out by
  // screen readers; a toast would sit behind an open modal dialog. After a few seconds the button resets.
  const copyLive = () => {
    let live = document.getElementById('JVCopyLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'JVCopyLive';
      live.className = 'jv-vh';
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    return live;
  };
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-jv-copy]');
    if (!button) return;
    const text = button.getAttribute('data-jv-copy') || '';
    if (!text) return;
    const ok = await JV.copyText(text);
    const label = button.querySelector('[data-jv-copy-label]') || button;
    if (!button.dataset.jvCopyText) button.dataset.jvCopyText = label.textContent;
    window.clearTimeout(button._jvCopyTimer);
    if (ok) {
      const message = button.getAttribute('data-copied-label') || JV.t('copied', 'Koden är kopierad');
      label.textContent = message;
      button.setAttribute('data-copied', '');
      const live = button.closest('dialog') || document.body;
      const region = copyLive();
      if (region.parentElement !== live) live.appendChild(region);
      region.textContent = '';
      window.setTimeout(() => { region.textContent = message; }, 30);
      button._jvCopyTimer = window.setTimeout(() => {
        label.textContent = button.dataset.jvCopyText;
        button.removeAttribute('data-copied');
      }, 4000);
    } else {
      // Copying is blocked in this browser: select the code so it can be copied by hand.
      const card = button.closest('.jv-pop__card, .jv-pop__codebox, [data-jv-copy-scope]');
      const code = card && card.querySelector('.jv-pop__code, [data-jv-wheel-code]');
      if (code) {
        const range = document.createRange();
        range.selectNodeContents(code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  });

  /* ------------------------------------------------------------------ theme editor */
  // Sections that own a dialog (cart drawer, popup, cookie banner) mark it with data-jv-editor-preview;
  // selecting the section in the editor opens it so it can be edited.
  if (designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      const dialog = event.target.querySelector('dialog[data-jv-editor-preview]');
      if (dialog) JV.openDialog(dialog);
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      const dialog = event.target.querySelector('dialog[data-jv-editor-preview]');
      if (dialog) JV.closeDialog(dialog);
    });
    ['shopify:section:load', 'shopify:section:unload'].forEach((name) =>
      document.addEventListener(name, () => window.setTimeout(syncScrollLock, 0))
    );
    document.addEventListener('shopify:section:load', (event) => {
      JV.reveal(event.target);
      JV.wishlist.sync(event.target);
    });
  }

  /* ------------------------------------------------------------------ boot */
  const boot = () => {
    JV.reveal(document);
    JV.wishlist.sync(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
